<template>
  <v-app
    v-if="hasReviewsList"
    data-reviews-filter
  >
    <ReviewsFilterTags
      v-show="isActiveTag"
      :chips="chipsFilter"
      @change:filter-chip="handleChangeTagFilter"
    />
    <div
      v-show="!isActiveTag"
    >
      <template
        v-if="isMobile"
      >
        <ReviewsFilterChip
          :selected="selected"
          :is-active-filter="isActiveFilter"
          data-qa="reviews_filter_chip"
          v-on="{
            'click:show': handleClickFilter,
            'click:close': handleClickCloseFilter,
          }"
        />
        <v-bottom-sheet
          v-model="isActiveDialog"
          @click:outside="handleClickOutside"
        >
          <ReviewsFilterList
            :reviews-list="reviewsList"
            @click:item="handleClickItem"
          />
        </v-bottom-sheet>
      </template>
      <template
        v-else
      >
        <v-menu
          left
          offset-y
          nudge-bottom="8"
          max-width="320"
        >
          <template #activator="{ on }">
            <div
              v-click-outside="handleClickOutside"
              :style="{ maxWidth: '320px' }"
              v-on="on"
            >
              <ReviewsFilterChip
                :selected="selected"
                :is-active-filter="isActiveFilter"
                data-qa="reviews_filter_chip"
                v-on="{
                  'click:show': handleClickFilter,
                  'click:close': handleClickCloseFilter,
                }"
              />
            </div>
          </template>
          <ReviewsFilterList
            :reviews-list="reviewsList"
            @click:item="handleClickItem"
          />
        </v-menu>
      </template>
    </div>
  </v-app>
</template>

<script setup lang="ts">
import {
  Ref,
  ref,
  computed,
  nextTick,
  onMounted,
} from 'vue'
import I18NextVue from 'i18next'
import setQueryParam from 'www/utils/set/setQueryParam'
import getQueryParam from 'www/utils/get/getQueryParam'
import deleteQueryParam from 'www/utils/delete/deleteQueryParam'
import YaGoals from 'www/modules/YandexGoals'
import type { Tag } from 'www/vue_components/common/ReviewsTags/types'
import type { Review, ChipFilter } from 'www/vue_components/common/ReviewsFilter/types'
import ReviewsFilterTags from 'www/vue_components/common/ReviewsFilter/components/ReviewsFilterTags.vue'
import ReviewsFilterChip from 'www/vue_components/common/ReviewsFilter/components/ReviewsFilterChip.vue'
import ReviewsFilterList from 'www/vue_components/common/ReviewsFilter/components/ReviewsFilterList.vue'

const { t, language } = I18NextVue
const options = { ns: language }

const props = withDefaults(defineProps<{
  activeTag?: Tag,
  pageType: 'doctor' | 'lpu' | ''
  reviewsList: Array<Review>
}>(), {
  pageType: '',
  activeTag: undefined,
  reviewsList: () => [],
})
const chipsFilter: Ref<ChipFilter[]> = ref([
  {
    isActive: getQueryParam('rates_category') === 'positive',
    value: 'positive',
    text: t('Понравилось', options),
    amount: 0,
  },
  {
    isActive: getQueryParam('rates_category') === 'negative',
    value: 'negative',
    text: t('Не понравилось', options),
    amount: 0,
  },
])
const isActiveTag = ref(Boolean(getQueryParam('tag')))
const reviewsList = ref(props.reviewsList)
const defaultValue = t('По оценке', options)
const defaultSelectedReview = reviewsList.value.find((item: Review) => item.isActive)
const defaultSelectedValue = defaultSelectedReview ? `${defaultSelectedReview.title} (${defaultSelectedReview.amount})` : defaultValue
const isMobile = window.MOBILE_VERSION
const selected = ref(defaultSelectedValue)
const isActiveDialog = ref(false)
const yaGoalsInstance = new YaGoals()
const isActiveFilter = computed(() => selected.value !== defaultValue)
const hasReviewsList = Boolean(props.reviewsList.find((item: Review) => item.value === 'all')?.amount)

function handleClickOutside() {
  if (isActiveDialog.value) {
    yaGoalsInstance.send({ name: 'reviews_list_click_outside_filter_list' })
  }
}

function redirectUser() {
  const {
    hash,
    origin,
    search,
    pathname,
  } = window.location
  const path = pathname.replace(/\/\d+\//, '/')
  const fullPath = `${origin}${path}${search}${hash}`

  window.location.assign(fullPath)

  if (!pathname.match(/\/\d+\//) && hash) {
    window.location.reload()
  }
}

function handleClickFilter() {
  isActiveDialog.value = !isActiveDialog.value

  if (props.pageType === 'doctor') {
    yaGoalsInstance.send({ name: 'reviews_list_filter_click_doctor_page' })
  }

  if (props.pageType === 'lpu') {
    yaGoalsInstance.send({ name: 'reviews_list_filter_click_lpu_page' })
  }
}

async function handleClickCloseFilter(event: Event) {
  if (isActiveFilter.value) {
    event.stopPropagation()

    reviewsList.value.forEach((i: Review) => {
      i.isActive = false
    })

    selected.value = defaultValue
    deleteQueryParam('rates_category')

    yaGoalsInstance.send({ name: 'reviews_list_click_filter_reset' })

    await nextTick()

    redirectUser()
  }
}

function handleClickItem(item: Review) {
  if (!getQueryParam('tag')) {
    yaGoalsInstance.send({ name: 'reviews_list_click_filter_list_lpu_without_tag' })
  }

  isActiveDialog.value = false
  selected.value = `${item.title} (${item.amount})`

  setQueryParam('rates_category', item.value)
  redirectUser()
}

function handleChangeTag(payload: { detail: { tag: Tag } }) {
  const { tag } = payload.detail

  deleteQueryParam('rates_category')
  selected.value = defaultValue
  reviewsList.value.forEach((item: Review) => { item.isActive = false })
  isActiveTag.value = tag.isActive
  chipsFilter.value.forEach((chip: ChipFilter) => {
    chip.isActive = false

    if (chip.value === 'positive') {
      chip.amount = tag.countLikedRates
    }

    if (chip.value === 'negative') {
      chip.amount = tag.countDislikedRates
    }
  })

  redirectUser()
}

function handleLoadTag(payload: { detail: { tag: Tag } }) {
  const { tag } = payload.detail

  if (!tag) {
    return
  }

  chipsFilter.value.forEach((chip: ChipFilter) => {
    if (chip.value === 'positive') {
      chip.amount = tag.countLikedRates
    }

    if (chip.value === 'negative') {
      chip.amount = tag.countDislikedRates
    }
  })
}

function handleChangeTagFilter(chip: ChipFilter) {
  yaGoalsInstance.send({ name: 'reviews_list_click_filter_list_lpu_with_tag' })

  if (!chip.isActive) {
    chipsFilter.value.forEach(i => { i.isActive = false })
  }

  chip.isActive = !chip.isActive
  chip.isActive ? setQueryParam('rates_category', chip.value) : deleteQueryParam('rates_category') // eslint-disable-line no-unused-expressions

  redirectUser()
}

onMounted(() => {
  window.addEventListener(
    'change-query-tag',
    payload => {
      handleChangeTag(payload as { detail: { tag: Tag } } & Event)
    },
  )
  window.addEventListener(
    'load-query-tag',
    payload => {
      handleLoadTag(payload as { detail: { tag: Tag } } & Event)
    },
  )
})
</script>

<style lang="scss">
@import '~www/themes/doctors/common/mixins';

#app[data-reviews-filter] {
  @include v-app-reset;
}
</style>

<style>
.vue-review-filter .v-menu__content {
  left: 0 !important;
}
</style>
